import React, { useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Calendar } from 'primereact/calendar';

import AdminProtectedRoute from '../../../components/admin/AdminProtectedRoute';
import AdminLayout from '../../../components/admin/AdminLayout';
import StreamBuilder from '../../../components/common/StreamBuilder';
import { BlocManagementTrips, TripViewState } from '../../../bloc/BlocManagementTrips';
import { Trip } from '../../../../types/cms.types';
import { CpnDetailTrip } from './components/CpnDetailTrip';

export const TripsManagementPage: React.FC = () => {
    // Bloc instance
    const [bloc] = useState(() => new BlocManagementTrips());

    // UI state
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [selectedVehicleType, setSelectedVehicleType] = useState<string>('');
    const [dateRange, setDateRange] = useState<Date[]>([]);
    const [lazyLoading, setLazyLoading] = useState<boolean>(false);

    // Detail dialog state
    const [showDetailDialog, setShowDetailDialog] = useState<boolean>(false);
    const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);

    // Refs
    const toast = useRef<Toast>(null);    // Load data on component mount
    useEffect(() => {
        bloc.initData();

        // Cleanup on unmount
        return () => {
            bloc.dispose();
        };
    }, [bloc]);

    // Status options
    const statusOptions = [
        { label: 'Tất cả trạng thái', value: '' },
        { label: 'Đã yêu cầu', value: 'requested' },
        { label: 'Đã nhận', value: 'accepted' },
        { label: 'Đang đến điểm đón', value: 'going_pickup' },
        { label: 'Đang di chuyển', value: 'in_progress' },
        { label: 'Hoàn thành', value: 'completed' },
        { label: 'Tài xế hủy', value: 'cancelled_by_driver' },
        { label: 'Khách hàng hủy', value: 'cancelled_by_user' }
    ];

    // Vehicle type options
    const vehicleTypeOptions = [
        { label: 'Tất cả loại xe', value: '' },
        { label: 'Ô tô', value: 'car' },
        { label: 'Xe máy', value: 'motorbike' }
    ];

    // Status severity mapping
    const getStatusSeverity = (status: string): "success" | "info" | "warning" | "danger" => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress':
            case 'going_pickup':
            case 'accepted': return 'info';
            case 'requested': return 'warning';
            case 'cancelled_by_driver':
            case 'cancelled_by_user': return 'danger';
            default: return 'info';
        }
    };

    // Status label mapping
    const getStatusLabel = (status: string): string => {
        const statusLabels: { [key: string]: string } = {
            requested: 'Đã yêu cầu',
            accepted: 'Đã nhận',
            going_pickup: 'Đang đến điểm đón',
            in_progress: 'Đang di chuyển',
            completed: 'Hoàn thành',
            cancelled_by_driver: 'Tài xế hủy',
            cancelled_by_user: 'Khách hàng hủy'
        };
        return statusLabels[status] || status;
    };

    // Filter handlers
    const handleStatusFilter = async (status: string) => {
        setSelectedStatus(status);
        await bloc.filterByStatus(status);
    };

    const handleVehicleTypeFilter = async (vehicleType: string) => {
        setSelectedVehicleType(vehicleType);
        await bloc.filterByVehicleType(vehicleType);
    };

    const handleDateRangeFilter = async (dates: Date[]) => {
        setDateRange(dates);

        // Check if we have valid dates and both are not null
        if (dates && dates.length === 2 && dates[0] && dates[1]) {
            const [startDate, endDate] = dates;

            // Format dates using local timezone to avoid UTC conversion issues
            const formatLocalDate = (date: Date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            // Set time to beginning of day for start date and end of day for end date
            // to ensure proper range filtering
            const startDateString = formatLocalDate(startDate) + ' 00:00:00';
            const endDateString = formatLocalDate(endDate) + ' 23:59:59';


            await bloc.filterByDateRange(startDateString, endDateString);
        } else if (!dates || dates.length === 0) {
            // Clear date filter
            await bloc.filterByDateRange();
        }
        // If dates array has null values or incomplete selection, do nothing
    };

    // Clear filters
    const clearFilters = async () => {
        setSelectedStatus('');
        setSelectedVehicleType('');
        setDateRange([]);
        await bloc.clearFilters();
    };

    // Page change handler for DataTable's onPage event
    const onPageChange = async (event: any) => {
        setLazyLoading(true);

        // If rows changed, reset to page 1
        if (event.rows !== bloc.currentPagination.limit) {
            await bloc.changePageSize(event.rows);
        } else {
            // DataTable's page is 0-based, but our API expects 1-based
            await bloc.changePage(event.page + 1);
        }

        setLazyLoading(false);
    };

    // Refresh handler
    const handleRefresh = async () => {
        await bloc.refresh();
    };

    // Delete handler
    const handleDelete = (trip: Trip) => {
        confirmDialog({
            message: `Bạn có chắc chắn muốn xóa chuyến đi từ "${trip.pickup_address}" đến "${trip.dropoff_address}"?`,
            header: 'Xác nhận xóa',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    await bloc.deleteTrip(trip.id);
                    toast.current?.show({
                        severity: 'success',
                        summary: 'Thành công',
                        detail: 'Đã xóa chuyến đi thành công',
                        life: 3000
                    });
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Lỗi',
                        detail: 'Không thể xóa chuyến đi',
                        life: 3000
                    });
                }
            }
        });
    };

    // View detail handler
    const handleViewDetail = (trip: Trip) => {
        setSelectedTrip(trip);
        setShowDetailDialog(true);
    };

    // Close detail dialog handler
    const handleCloseDetailDialog = () => {
        setShowDetailDialog(false);
        setSelectedTrip(null);
    };

    // Column templates
    const customerTemplate = (trip: Trip) => (
        <div>
            <div className="font-medium text-gray-900">
                {trip.customer?.full_name || 'Chưa có thông tin'}
            </div>
            <div className="text-sm text-gray-500">
                {trip.customer?.phone_number || 'Chưa có SĐT'}
            </div>
        </div>
    );

    const driverTemplate = (trip: Trip) => (
        <div>
            <div className="font-medium text-gray-900">
                {trip.driver?.full_name || 'Chưa phân công'}
            </div>
            <div className="text-sm text-gray-500">
                {trip.driver?.phone_number || ''}
            </div>
        </div>
    );

    const routeTemplate = (trip: Trip) => (
        <div className="max-w-xs">
            <div className="text-sm font-medium text-gray-900 truncate" title={trip.pickup_address}>
                <i className="pi pi-circle-fill text-green-500 mr-1"></i>
                {trip.pickup_address}
            </div>
            <div className="text-sm text-gray-500 truncate mt-1" title={trip.dropoff_address}>
                <i className="pi pi-map-marker text-red-500 mr-1"></i>
                {trip.dropoff_address}
            </div>
        </div>
    );

    const vehicleTypeTemplate = (trip: Trip) => (
        <Tag
            value={trip.vehicle_type === 'car' ? 'Ô tô' : 'Xe máy'}
            severity={trip.vehicle_type === 'car' ? 'info' : 'success'}
            icon={trip.vehicle_type === 'car' ? 'pi pi-car' : 'pi pi-truck'}
        />
    );

    const statusTemplate = (trip: Trip) => (
        <Tag
            value={getStatusLabel(trip.status)}
            severity={getStatusSeverity(trip.status)}
        />
    );

    const priceTemplate = (trip: Trip) => (
        <span className="font-medium">
            {parseFloat(trip.price || '0').toLocaleString('vi-VN')}đ
        </span>
    );

    const dateTemplate = (trip: Trip) => (
        <span className="text-sm text-gray-600">
            {new Date(trip.created_at).toLocaleString('vi-VN')}
        </span>
    );


    const actionTemplate = (trip: Trip) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-eye"
                className="p-button-text p-button-info"
                tooltip="Xem chi tiết"
                onClick={() => handleViewDetail(trip)}
            />
            <Button
                icon="pi pi-trash"
                className="p-button-text p-button-danger"
                tooltip="Xóa"
                onClick={() => handleDelete(trip)}
            />
        </div>
    ); return (
        <AdminProtectedRoute
            requiredModule="trip_management"
            requiredSubModule="trip_list"
            requiredPermission="view">
            <AdminLayout>
                <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Toast ref={toast} />
                    <ConfirmDialog />

                    {/* Table Container */}
                    <div style={{ flex: 1, minHeight: 0, background: 'white' }}>
                        <StreamBuilder
                            stream={bloc.getStream('info')}
                            initialData={{
                                trips: [],
                                pagination: { total: 0, page: 1, limit: 10, totalPages: 0 },
                                loading: false,
                                error: null,
                                filters: {},
                                hasData: false
                            }}
                            builder={(snapshot: any) => {
                                const state: TripViewState = snapshot.data;

                                if (state.error) {
                                    return (
                                        <div style={{ padding: '24px' }}>
                                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '16px' }}>
                                                <div style={{ display: 'flex' }}>
                                                    <i className="pi pi-exclamation-triangle" style={{ color: '#f87171', marginRight: '8px' }}></i>
                                                    <div style={{ color: '#991b1b' }}>
                                                        <h3 style={{ fontSize: '0.875rem', fontWeight: '500' }}>Có lỗi xảy ra</h3>
                                                        <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>{state.error}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }

                                // Header template with filters - more compact and wider
                                const headerTemplate = (
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '16px',
                                        alignItems: 'center',
                                        padding: '16px 24px',
                                        borderBottom: '1px solid #e5e7eb',
                                        background: '#f9fafb',
                                        width: '100%'
                                    }}>


                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 250px', minWidth: '250px' }}>
                                            <i className="pi pi-calendar" style={{ color: '#6b7280', fontSize: '0.875rem' }}></i>
                                            <Calendar
                                                value={dateRange}
                                                onChange={(e) => handleDateRangeFilter(e.value as Date[])}
                                                selectionMode="range"
                                                readOnlyInput
                                                placeholder="Chọn thời gian"
                                                style={{
                                                    flex: 1,
                                                    fontSize: '0.875rem',
                                                    minWidth: '200px'
                                                }}
                                                inputStyle={{
                                                    fontSize: '0.875rem',
                                                    padding: '8px 12px'
                                                }}
                                            />
                                        </div>

                                        <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                                            <Button
                                                icon="pi pi-filter-slash"
                                                onClick={clearFilters}
                                                className="p-button-outlined p-button-secondary p-button-sm"
                                                tooltip="Xóa bộ lọc"
                                                style={{ padding: '6px 12px' }}
                                            />
                                            <Button
                                                icon="pi pi-refresh"
                                                onClick={handleRefresh}
                                                className="p-button-outlined p-button-sm"
                                                tooltip="Làm mới"
                                                style={{ padding: '6px 12px' }}
                                            />
                                        </div>
                                    </div>
                                );

                                return (
                                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        {/* Table */}
                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                            <DataTable
                                                scrollHeight='calc(100vh - 200px)'
                                                value={state.trips}
                                                loading={state.loading || lazyLoading}
                                                loadingIcon="pi pi-spinner pi-spin"
                                                className="trips-management-table"
                                                emptyMessage="Không tìm thấy chuyến đi nào"
                                                responsiveLayout="scroll"
                                                scrollable

                                                size="small"
                                                stripedRows
                                                header={headerTemplate}
                                                showGridlines
                                                paginator={state.pagination.total > 0}
                                                rows={state.pagination.limit}
                                                totalRecords={state.pagination.total}
                                                first={(state.pagination.page - 1) * state.pagination.limit}
                                                onPage={onPageChange}
                                                rowsPerPageOptions={[10, 20, 50]}
                                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
                                                currentPageReportTemplate="Hiển thị {first} đến {last} trong tổng số {totalRecords} bản ghi"
                                            >
                                                <Column
                                                    field="customer"
                                                    header="Khách hàng"

                                                    body={customerTemplate}
                                                    style={{ minWidth: '200px', }}
                                                    frozen
                                                />
                                                <Column
                                                    field="driver"
                                                    header="Tài xế"

                                                    body={driverTemplate}
                                                    style={{ minWidth: '180px' }}
                                                />
                                                <Column
                                                    field="route"
                                                    header="Tuyến đường"
                                                    body={routeTemplate}
                                                    style={{ minWidth: '300px' }}
                                                />
                                                <Column
                                                    field="vehicle_type"
                                                    header="Loại xe"

                                                    body={vehicleTypeTemplate}
                                                    style={{ minWidth: '100px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedVehicleType}
                                                            options={vehicleTypeOptions}
                                                            onChange={(e) => handleVehicleTypeFilter(e.value)}
                                                            placeholder="Tất cả loại xe"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="status"
                                                    header="Trạng thái"

                                                    body={statusTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    filter
                                                    showFilterMenuOptions={false}
                                                    showClearButton={false}
                                                    filterElement={
                                                        <Dropdown
                                                            value={selectedStatus}
                                                            options={statusOptions}
                                                            onChange={(e) => handleStatusFilter(e.value)}
                                                            placeholder="Tất cả trạng thái"
                                                            style={{ width: '100%', fontSize: '0.75rem' }}
                                                            className="p-column-filter"
                                                        />
                                                    }
                                                />
                                                <Column
                                                    field="price"
                                                    header="Giá"
                                                    body={priceTemplate}
                                                    style={{ minWidth: '100px' }}
                                                />

                                                <Column
                                                    field="created_at"
                                                    header="Thời gian tạo"
                                                    body={dateTemplate}
                                                    style={{ minWidth: '150px' }}
                                                />
                                                <Column
                                                    header="Thao tác"
                                                    body={actionTemplate}
                                                    style={{ minWidth: '120px' }}
                                                    frozen
                                                    alignFrozen="right"
                                                />
                                            </DataTable>
                                        </div>

                                        {/* Show message when no data and not loading */}
                                        {!state.loading && state.trips.length === 0 && (
                                            <div style={{ flexShrink: 0, padding: '32px', textAlign: 'center' }}>
                                                <div style={{ color: '#6b7280' }}>
                                                    <i className="pi pi-info-circle" style={{ fontSize: '2rem', marginBottom: '12px', display: 'block' }}></i>
                                                    <p style={{ fontSize: '1.125rem', fontWeight: '500' }}>Chưa có dữ liệu chuyến đi</p>
                                                    <p style={{ fontSize: '0.875rem', marginTop: '4px' }}>Hệ thống chưa có chuyến đi nào được tạo.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            }}
                        />
                    </div>
                </div>
            </AdminLayout>

            {/* Detail Dialog */}
            <CpnDetailTrip
                visible={showDetailDialog}
                onHide={handleCloseDetailDialog}
                trip={selectedTrip}
            />
        </AdminProtectedRoute>
    );
};
